import { useEffect, useState } from "react";
import api, { formatApiError, catIcon } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Upload, Camera, PencilLine, Loader2, Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  provider_id: "", provider_name: "", account_number: "", bill_number: "",
  customer_name: "", amount: "", currency: "USD", due_date: "", issue_date: "", plan: "",
  storage_path: "", content_type: "", original_filename: "", extracted: null,
};

export default function AddBillDialog({ open, onOpenChange, categories, onSaved, preselected }) {
  const [category, setCategory] = useState(null);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(emptyForm); setShowForm(false);
      setCategory(preselected || null);
    }
  }, [open, preselected]);

  useEffect(() => {
    if (category) {
      api.get(`/providers?category_slug=${category.slug}`).then((r) => setProviders(r.data));
      if (category.coming_soon) return;
    }
  }, [category]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bills/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setForm((f) => ({
        ...f,
        provider_name: ex.provider || f.provider_name,
        account_number: ex.account_number || f.account_number,
        bill_number: ex.bill_number || f.bill_number,
        customer_name: ex.customer_name || f.customer_name,
        amount: ex.amount != null ? String(ex.amount) : f.amount,
        currency: ex.currency || f.currency,
        due_date: ex.due_date || f.due_date,
        issue_date: ex.issue_date || f.issue_date,
        storage_path: data.storage_path, content_type: data.content_type,
        original_filename: data.original_filename, extracted: ex,
      }));
      setShowForm(true);
      toast.success("Bill scanned — please verify the details.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setExtracting(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      const prov = providers.find((p) => p.id === form.provider_id);
      await api.post("/bills", {
        category_slug: category.slug,
        provider_id: form.provider_id || null,
        provider_name: prov?.name || form.provider_name || null,
        account_number: form.account_number || null,
        bill_number: form.bill_number || null,
        customer_name: form.customer_name || null,
        amount: parseFloat(form.amount),
        currency: form.currency || "USD",
        due_date: form.due_date || null,
        issue_date: form.issue_date || null,
        plan: form.plan || null,
        storage_path: form.storage_path || null,
        content_type: form.content_type || null,
        original_filename: form.original_filename || null,
        extracted: form.extracted || null,
      });
      toast.success("Bill added!");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const activeCats = categories.filter((c) => !c.coming_soon);
  const soonCats = categories.filter((c) => c.coming_soon);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-bill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {category && <button onClick={() => { setCategory(null); setShowForm(false); }} data-testid="add-bill-back"><ArrowLeft className="w-5 h-5" /></button>}
            {category ? `Add ${category.name} bill` : "Add a bill"}
          </DialogTitle>
          <DialogDescription>{category ? "Choose how to add your bill." : "Pick a category to get started."}</DialogDescription>
        </DialogHeader>

        {!category && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {activeCats.map((c) => {
                const Icon = catIcon(c.icon);
                return (
                  <button key={c.slug} onClick={() => setCategory(c)} data-testid={`pick-cat-${c.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                    <Icon className="w-6 h-6 text-emerald-600" />
                    <span className="text-xs font-semibold text-slate-700 text-center">{c.name}</span>
                  </button>
                );
              })}
            </div>
            {soonCats.length > 0 && <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Coming soon</p>}
            <div className="grid grid-cols-3 gap-3">
              {soonCats.map((c) => {
                const Icon = catIcon(c.icon);
                return (
                  <div key={c.slug} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-dashed border-slate-200 opacity-50">
                    <Icon className="w-6 h-6 text-slate-400" />
                    <span className="text-xs font-semibold text-slate-500 text-center">{c.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {category && category.coming_soon && (
          <div className="text-center py-8">
            <p className="font-display font-bold text-slate-900">{category.name} is coming soon</p>
            <p className="text-sm text-slate-500 mt-1">Payment integration for this category isn't available yet.</p>
          </div>
        )}

        {category && !category.coming_soon && !showForm && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <label className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-emerald-300 hover:bg-emerald-50 cursor-pointer transition-colors">
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} disabled={extracting} data-testid="add-bill-upload" />
                <Upload className="w-6 h-6 text-emerald-600" />
                <span className="text-xs font-semibold text-slate-700">Upload</span>
              </label>
              <label className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} disabled={extracting} data-testid="add-bill-scan" />
                <Camera className="w-6 h-6 text-slate-700" />
                <span className="text-xs font-semibold text-slate-700">Scan</span>
              </label>
              <button onClick={() => setShowForm(true)} data-testid="add-bill-manual"
                className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:bg-slate-50 transition-colors">
                <PencilLine className="w-6 h-6 text-slate-700" />
                <span className="text-xs font-semibold text-slate-700">Manual</span>
              </button>
            </div>
            {extracting && (
              <div className="flex items-center justify-center gap-2 text-emerald-600 py-4">
                <Loader2 className="w-5 h-5 animate-spin" /> <span className="font-semibold text-sm">Reading your bill with AI...</span>
              </div>
            )}
            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1"><Sparkles className="w-3 h-3" /> Upload or scan and our AI fills the details for you</p>
          </div>
        )}

        {category && !category.coming_soon && showForm && (
          <form onSubmit={save} className="space-y-3">
            {form.extracted && (
              <div className="flex items-center gap-2 text-emerald-600 text-sm bg-emerald-50 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4" /> AI extracted these — please verify & edit.
              </div>
            )}
            <div>
              <Label className="font-semibold text-sm">Provider</Label>
              <Select value={form.provider_id} onValueChange={(v) => setForm({ ...form, provider_id: v })}>
                <SelectTrigger className="mt-1 bg-slate-50" data-testid="add-bill-provider"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>
                  {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="font-semibold text-sm">Account number</Label><Input value={form.account_number} onChange={set("account_number")} data-testid="add-bill-account" className="mt-1 bg-slate-50" /></div>
              <div><Label className="font-semibold text-sm">Bill number</Label><Input value={form.bill_number} onChange={set("bill_number")} className="mt-1 bg-slate-50" /></div>
            </div>
            <div><Label className="font-semibold text-sm">Customer name</Label><Input value={form.customer_name} onChange={set("customer_name")} className="mt-1 bg-slate-50" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="font-semibold text-sm">Amount ($)</Label><Input type="number" step="0.01" required value={form.amount} onChange={set("amount")} data-testid="add-bill-amount" className="mt-1 bg-slate-50" /></div>
              <div><Label className="font-semibold text-sm">Due date</Label><Input value={form.due_date} onChange={set("due_date")} placeholder="e.g. Aug 15" className="mt-1 bg-slate-50" /></div>
            </div>
            <Button type="submit" disabled={saving} data-testid="add-bill-save" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold py-6">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save bill"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
